import React, { useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const DetailTab = ({ data }) => {
  const classes = useStyles();

  const detailData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const flattenedData = [];

    data.forEach((clientGroup) => {
      if (clientGroup.items && clientGroup.items.length > 0) {
        clientGroup.items.forEach((item) => {
          flattenedData.push({
            id: item.orderNumber,
            date: item.date,
            client: clientGroup.client,
            code: item.code,
            product: item.product,
            quantity: item.qtyProduct || 1,
            price: item.price,
            discount: item.discount || 0,
            suma: item.suma,
            calculatedPrice: item.priceCalc,
            discountAmount: item.discountPrice,
            hasError: (item.defect?.includes(true) ||
              item.defectNotice?.length > 0 || !item.pay),
            hasRaintake: item.raintake?.includes('reintake')
          });
        });
      }
    });

    return flattenedData.sort((a, b) => {
      const dateComparison = new Date(a.date) - new Date(b.date);
      if (dateComparison !== 0) return dateComparison;
      return a.id - b.id;
    });
  }, [data]);

  const detailTotals = useMemo(() => {
    return detailData.reduce((acc, item) => ({
      quantity: acc.quantity + item.quantity,
      price: acc.price + item.price,
      discount: acc.discount + item.discount,
      suma: acc.suma + item.suma,
      calculatedPrice: acc.calculatedPrice + item.calculatedPrice,
      discountAmount: acc.discountAmount + item.discountAmount
    }), {
      quantity: 0,
      price: 0,
      discount: 0,
      suma: 0,
      calculatedPrice: 0,
      discountAmount: 0
    });
  }, [detailData]);

  const formatNumber = (value) => {
    return new Intl.NumberFormat('uk-UA', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('uk-UA');
    } catch {
      return dateString;
    }
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell style={{ width: '80px' }}>ID</TableCell>
            <TableCell style={{ width: '100px' }}>Дата</TableCell>
            <TableCell style={{ width: '200px' }}>Клієнт</TableCell>
            <TableCell style={{ width: '80px' }}>Код</TableCell>
            <TableCell style={{ width: '350px' }}>Назва продукту</TableCell>
            <TableCell style={{ width: '80px' }}>Кількість</TableCell>
            <TableCell style={{ width: '100px' }}>Ціна</TableCell>
            <TableCell style={{ width: '100px' }}>Знижка</TableCell>
            <TableCell style={{ width: '100px' }}>Сума</TableCell>
            <TableCell style={{ width: '120px' }}>Ціна розрахунку</TableCell>
            <TableCell style={{ width: '120px' }}>Сума знижки</TableCell>
          </TableRow>
          <TableRow className={classes.grandTotalRow}>
            <TableCell><strong>Загально:</strong></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell align="center"><strong>{detailTotals.quantity}</strong></TableCell>
            <TableCell align="right"><strong>{formatNumber(detailTotals.price)}</strong></TableCell>
            <TableCell align="right"><strong>{formatNumber(detailTotals.discount)}</strong></TableCell>
            <TableCell align="right"><strong>{formatNumber(detailTotals.suma)}</strong></TableCell>
            <TableCell align="right"><strong>{formatNumber(detailTotals.calculatedPrice)}</strong></TableCell>
            <TableCell align="right"><strong>{formatNumber(detailTotals.discountAmount)}</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {detailData.map((item, index) => (
            <TableRow
              key={`detail-${index}`}
              className={item.hasError ? classes.errorRow : item.hasRaintake
                ? classes.raintakeRow
                : classes.dataRow}
            >
              <TableCell>{item.id}</TableCell>
              <TableCell>{formatDate(item.date)}</TableCell>
              <TableCell>{item.client}</TableCell>
              <TableCell>{item.code}</TableCell>
              <TableCell>{item.product}</TableCell>
              <TableCell align="center">{item.quantity}</TableCell>
              <TableCell align="right">{formatNumber(item.price)}</TableCell>
              <TableCell align="right">{formatNumber(item.discount)}</TableCell>
              <TableCell align="right">{formatNumber(item.suma)}</TableCell>
              <TableCell align="right">{formatNumber(item.calculatedPrice)}</TableCell>
              <TableCell align="right">{formatNumber(item.discountAmount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DetailTab;


export const useStyles = makeStyles((theme) => ({
  tableContainer: {
    border: '1px solid #000',
    height: 'calc(100vh - 310px)',
        minHeight: '250px',

    overflow: 'auto',
    '& .MuiPaper-root': {
      boxShadow: 'none'
    }
  },
  table: {
    borderCollapse: 'collapse',
    '& .MuiTableCell-root': {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      padding: '4px 8px',
      border: '1px solid #000',
      lineHeight: '1.2',
      verticalAlign: 'middle'
    },
    '& .MuiTableCell-head': {
      backgroundColor: '#DDDDDD',
      fontWeight: 'bold',
      textAlign: 'center',
      fontSize: '11px',
      padding: '6px 4px',
      position: 'sticky',
      top: 0,
      zIndex: 100
    }
  },
  grandTotalRow: {
    position: 'sticky',
    top: '30px',
    zIndex: 50,
    '& .MuiTableCell-root': {
      backgroundColor: '#DDDDDD',
      fontWeight: 'bold',
      border: '1px solid #000',
      fontSize: '11px'
    }
  },
  dataRow: {
    '& .MuiTableCell-root': {
      backgroundColor: '#ffffff'
    },
    '&:nth-child(even)': {
      '& .MuiTableCell-root': {
        backgroundColor: '#f9f9f9'
      }
    }
  },
  errorRow: {
    '& .MuiTableCell-root': {
      backgroundColor: '#FFCCCC !important'
    }
  },
  raintakeRow: {
    '& .MuiTableCell-root': {
      backgroundColor: '#ffd699 !important'
    }
  },
}));
